"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Copy, Download, Trash2, Eye, Code } from "lucide-react"
import { toast } from "sonner"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

const sampleMarkdown = `# Markdown Preview

Welcome to the **Markdown Preview** tool! This tool supports GitHub Flavored Markdown (GFM).

## Features

- **Bold text** and *italic text*
- ~~Strikethrough~~ text
- Inline \`code\` formatting
- [Links](https://example.com)

### Code Blocks

\`\`\`javascript
function greet(name) {
  console.log(\`Hello, \${name}!\`);
}
greet("World");
\`\`\`

### Tables

| Feature | Supported |
|---------|-----------|
| Tables | ✅ |
| Task Lists | ✅ |
| Autolinks | ✅ |

### Task Lists

- [x] Learn Markdown
- [x] Write documentation
- [ ] Share with team

### Blockquotes

> This is a blockquote.
> It can span multiple lines.

### Images

Images can be embedded using: \`![alt text](url)\`

---

**Try editing this content!** The preview updates in real-time.
`

export function MarkdownPreview() {
  const [markdown, setMarkdown] = useState(sampleMarkdown)
  const [viewMode, setViewMode] = useState<"split" | "preview" | "edit">("split")

  const copyMarkdown = () => {
    navigator.clipboard.writeText(markdown)
    toast.success("Markdown copied to clipboard")
  }

  const downloadMarkdown = () => {
    const blob = new Blob([markdown], { type: "text/markdown" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "document.md"
    a.click()
    URL.revokeObjectURL(url)
  }

  const clearContent = () => {
    setMarkdown("")
  }

  const loadSample = () => {
    setMarkdown(sampleMarkdown)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Markdown Preview</CardTitle>
        <CardDescription>
          Write and preview Markdown with GitHub Flavored Markdown (GFM) support including tables, task lists, and code blocks.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as typeof viewMode)}>
            <TabsList>
              <TabsTrigger value="split">
                Split
              </TabsTrigger>
              <TabsTrigger value="edit">
                <Code className="h-4 w-4 mr-2" />
                Edit
              </TabsTrigger>
              <TabsTrigger value="preview">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadSample}>
              Load Sample
            </Button>
            <Button variant="outline" size="sm" onClick={clearContent}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
            <Button variant="outline" size="sm" onClick={copyMarkdown}>
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={downloadMarkdown}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>

        {/* Editor and Preview */}
        <div className={`grid gap-4 ${viewMode === "split" ? "md:grid-cols-2" : ""}`}>
          {/* Editor */}
          {(viewMode === "split" || viewMode === "edit") && (
            <div className="space-y-2">
              {viewMode === "split" && <Label>Markdown</Label>}
              <Textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                placeholder="Write your markdown here..."
                className="font-mono text-sm min-h-[500px] resize-y"
              />
            </div>
          )}

          {/* Preview */}
          {(viewMode === "split" || viewMode === "preview") && (
            <div className="space-y-2">
              {viewMode === "split" && <Label>Preview</Label>}
              <div className="border rounded-lg p-4 min-h-[500px] overflow-auto prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Style overrides for better appearance
                    h1: ({ children }) => (
                      <h1 className="text-2xl font-bold mt-6 mb-4 pb-2 border-b">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-xl font-semibold mt-5 mb-3">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-lg font-semibold mt-4 mb-2">{children}</h3>
                    ),
                    p: ({ children }) => (
                      <p className="my-2">{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => (
                      <li className="ml-2">{children}</li>
                    ),
                    code: ({ className, children, ...props }) => {
                      const isInline = !className
                      if (isInline) {
                        return (
                          <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                            {children}
                          </code>
                        )
                      }
                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      )
                    },
                    pre: ({ children }) => (
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4 text-sm">
                        {children}
                      </pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-muted-foreground/30 pl-4 my-4 italic text-muted-foreground">
                        {children}
                      </blockquote>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-4">
                        <table className="w-full border-collapse border">{children}</table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="border px-3 py-2 bg-muted font-semibold text-left">{children}</th>
                    ),
                    td: ({ children }) => (
                      <td className="border px-3 py-2">{children}</td>
                    ),
                    a: ({ href, children }) => (
                      <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                    hr: () => <hr className="my-6 border-muted" />,
                    input: ({ type, checked, ...props }) => {
                      if (type === "checkbox") {
                        return (
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            className="mr-2 rounded"
                            {...props}
                          />
                        )
                      }
                      return <input type={type} {...props} />
                    },
                  }}
                >
                  {markdown || "*Start typing to see preview...*"}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{markdown.length} characters</span>
          <span>{markdown.split(/\s+/).filter(Boolean).length} words</span>
          <span>{markdown.split("\n").length} lines</span>
        </div>
      </CardContent>
    </Card>
  )
}
